import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import ListRow from "../components/ListRow";
import MediaIndicator from "../components/MediaIndicator";
import StatusChip from "../components/StatusChip";
import EmptyState from "../components/EmptyState";
import Button from "../components/Button";
import { SearchIcon } from "../components/icons";
import { fmt } from "../player/MiniPlayer";
import { getLatestFeed, groupFeed, facetCounts, isNewItem, FEED_PAGE, FEED_CAP } from "../api/latestFeed";
import { paths } from "../routing/paths";

/*
 * Latest classes per `Latest Classes.dc.html` (handoff README §6) —
 * replaces the flat 100-row legacy list. Newest-first feed grouped by day
 * with Hebrew-date headings; "New in" single-select filter chips faceted
 * from the feed itself (All first, then categories by new-class count
 * descending); 25 classes per page, "Show earlier classes" appends 25 to a
 * 100 cap, then the footer points at categories for older material; filter
 * changes reset pagination. The one new element is MediaIndicator
 * (audio / video / combined "A + V"); rows are the shared ListRow.
 */

export default function LatestClassesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading
  const [filter, setFilter] = useState("all"); // 'all' | category slug
  const [visible, setVisible] = useState(FEED_PAGE);

  useEffect(() => {
    let cancelled = false;
    getLatestFeed().then((rows) => !cancelled && setItems(rows));
    return () => {
      cancelled = true;
    };
  }, []);

  const facets = useMemo(() => facetCounts(items || []), [items]);
  const filtered = useMemo(
    () => (items || []).filter((i) => (filter === "all" ? true : i.category?.slug === filter)),
    [items, filter]
  );
  const shown = filtered.slice(0, Math.min(visible, FEED_CAP));
  const groups = useMemo(() => groupFeed(shown), [shown]);
  const hasMore = shown.length < filtered.length;

  const selectFilter = (value) => {
    setFilter(value);
    setVisible(FEED_PAGE); // filter changes reset pagination
  };

  const chipClass = (active) =>
    active
      ? "flex-shrink-0 font-sans text-[14px] font-bold leading-5 text-white bg-ic-blue border border-ic-blue px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] focus-visible:outline-none focus-visible:shadow-ic-focus"
      : "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft";

  const rowMeta = (item) =>
    [item.durationSeconds != null ? fmt(item.durationSeconds) : null, item.hasSourcePdf ? "Source PDF" : null]
      .filter(Boolean)
      .join(" · ") || null;

  const rowChip = (item) =>
    item.category || isNewItem(item) ? (
      <span className="inline-flex items-center gap-2">
        {item.category && (
          <Link
            to={item.category.type === "series" ? paths.series(item.category.slug) : paths.permalink(item.category.slug)}
            className="bg-ic-lightGray text-ic-body text-[11px] font-semibold tracking-[0.5px] leading-4 px-2.5 py-0.5 rounded-xl whitespace-nowrap no-underline transition-colors duration-150 ease-out hover:bg-ic-borderGray hover:text-ic-navy hover:no-underline"
          >
            {item.category.title}
          </Link>
        )}
        {isNewItem(item) && <StatusChip kind="new" size="sm">NEW</StatusChip>}
      </span>
    ) : undefined;

  return (
    <PublicLayout>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Latest classes</span>
        </nav>

        {/* Hero */}
        <section
          className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex gap-5 items-start flex-wrap"
          style={{ padding: "clamp(20px, 5vw, 32px)" }}
        >
          <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-ic-navyCard hidden xs:flex items-center justify-center">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.5 2" />
            </svg>
          </div>
          <div className="flex-1 min-w-[240px] flex flex-col gap-2">
            <h1 className="m-0 font-bold text-white tracking-[-0.4px]" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
              Latest classes
            </h1>
            <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">
              Every new class as it's posted, from the daily Hayom Yom to Sichos, Farbrengens, and more.
            </p>
          </div>
        </section>

        {/* "New in" filter chips — All first, then categories by count desc */}
        {facets.length > 0 && (
          <div className="ic-rail flex gap-2 overflow-x-auto items-center">
            <span className="flex-shrink-0 text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">New in</span>
            <button type="button" aria-pressed={filter === "all"} onClick={() => selectFilter("all")} className={chipClass(filter === "all")}>
              All
            </button>
            {facets.map((f) => (
              <button
                key={f.slug}
                type="button"
                aria-pressed={filter === f.slug}
                onClick={() => selectFilter(f.slug)}
                className={chipClass(filter === f.slug)}
              >
                {f.title} · {f.count}
              </button>
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {items === null && (
          <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading latest classes">
            <div className="h-7 w-40 rounded bg-ic-lightGray animate-pulse" />
            <div className="h-64 rounded-xl bg-ic-lightGray animate-pulse" />
            <div className="h-7 w-32 rounded bg-ic-lightGray animate-pulse" />
            <div className="h-64 rounded-xl bg-ic-lightGray animate-pulse" />
          </div>
        )}

        {/* Day-grouped feed */}
        {items !== null &&
          groups.map((g, gi) => (
            <section key={`${g.label}-${gi}`} className="flex flex-col gap-3">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(20px, 4.5vw, 24px)", lineHeight: 1.3 }}>
                  {g.label}
                </h2>
                {g.hebrewDate && <span className="text-[14px] leading-5 text-ic-slate">{g.hebrewDate}</span>}
              </div>
              <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                {g.rows.map((item, i) => (
                  <ListRow
                    key={item.id}
                    indicator={<MediaIndicator media={item.media || "audio"} />}
                    chip={rowChip(item)}
                    title={
                      <Link to={paths.permalink(item.slug)} className="text-inherit no-underline hover:text-ic-blue hover:no-underline">
                        {item.title}
                      </Link>
                    }
                    meta={rowMeta(item)}
                    actionLabel={`Play ${item.title}`}
                    onAction={() => navigate(paths.permalink(item.slug))}
                    last={i === g.rows.length - 1}
                    className="!py-3.5 !pl-4 !pr-4 !gap-3"
                  />
                ))}
              </div>
            </section>
          ))}

        {/* Load more + footer line */}
        {items !== null && filtered.length > 0 && (
          <div className="flex flex-col items-center gap-2 pb-2">
            {hasMore && (
              <button
                type="button"
                onClick={() => setVisible((v) => Math.min(v + FEED_PAGE, FEED_CAP))}
                className="bg-transparent text-ic-blue font-sans text-[16px] font-bold leading-6 h-12 px-8 border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:bg-[rgba(24,108,206,0.05)] focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
              >
                Show earlier classes
              </button>
            )}
            <span className="text-[14px] leading-5 text-ic-slate text-center">
              {hasMore
                ? `Showing the latest ${shown.length} of ${filtered.length} classes`
                : `Showing all ${filtered.length} classes · older classes live in their categories`}
            </span>
          </div>
        )}

        {/* Designed empty state — never blank */}
        {items !== null && filtered.length === 0 && (
          <EmptyState
            icon={<SearchIcon size={28} />}
            title={filter === "all" ? "No classes in the feed right now" : "Nothing new here in this window"}
            body="With 15,000+ classes in the archive, what you're looking for almost certainly exists — search will find it."
            actions={
              <span className="inline-flex gap-3 flex-wrap justify-center">
                {filter !== "all" && (
                  <Button size="lg" onClick={() => selectFilter("all")}>
                    Show all latest classes
                  </Button>
                )}
                <Link to={paths.search()}>
                  <Button size="lg" variant={filter !== "all" ? "ghost" : "primary"}>
                    Search the archive
                  </Button>
                </Link>
              </span>
            }
          />
        )}
      </main>
    </PublicLayout>
  );
}
